import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';

export interface QuantitySelectionModalData {
  /** Title to show above the picker (e.g. event/ticket name). */
  productName?: string;
  /** Current price per ticket, used for the running total preview. */
  unitPrice?: number;
  /** ISO currency code for the price preview. */
  currencyCode?: string;
  /** Initial quantity (defaults to 1). */
  initialQuantity?: number;
  /** Minimum allowed quantity (defaults to 1). */
  min?: number;
  /** Maximum allowed quantity. Omit for no max. */
  max?: number;
}

/**
 * Lightweight modal that asks the user how many tickets they want before
 * adding to the cart. Used when the selected ticket type has no per-attendee
 * registration fields, so we don't need to open the full AddToCart modal but
 * still want to give the user control over the quantity.
 *
 * Closes with the chosen quantity (number) on confirm, or `undefined` on cancel.
 */
@Component({
  selector: 'hypenation-tickets-quantity-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzInputNumberModule],
  templateUrl: './quantity-selection-modal.component.html',
})
export class QuantitySelectionModalComponent implements OnInit {
  readonly nzModalData: QuantitySelectionModalData = inject(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);

  quantity = 1;
  min = 1;
  max: number | null = null;

  ngOnInit(): void {
    this.min = this.nzModalData?.min ?? 1;
    this.max = this.nzModalData?.max ?? null;
    const initial = this.nzModalData?.initialQuantity ?? 1;
    this.quantity = this.clampQuantity(initial);
  }

  get total(): number {
    const unit = this.nzModalData?.unitPrice ?? 0;
    return unit * this.quantity;
  }

  confirm(): void {
    this.quantity = this.clampQuantity(this.quantity);
    this._modalRef.close(this.quantity);
  }

  private clampQuantity(value: number): number {
    const sanitized = Number.isFinite(value) ? Math.trunc(value) : this.min;
    if (this.max !== null) {
      return Math.min(this.max, Math.max(this.min, sanitized));
    }
    return Math.max(this.min, sanitized);
  }

  cancel(): void {
    this._modalRef.close();
  }
}
